"use client"

import { SurveyContent, SurveyJSONValue } from "@/interfaces/survey_types"
import { hasKey } from "@/utils/validators"
import { create } from "zustand"
import { devtools } from "zustand/middleware"

type FieldArrayValue = string | number

interface SurveyStore {
  survey: SurveyContent | null
  fields: Record<string, SurveyJSONValue>
  currentStep: string
  history: string[]
  retries: number
  isPending: boolean
  setSurvey: (data: SurveyContent) => void
  setStep: (step: string) => void
  previousStep: () => void
  setField: (field: string, value: SurveyJSONValue) => void
  setArrayField: (field: string, value: FieldArrayValue) => void
  removeRetry: () => void
  setIsPending: (value: boolean) => void
  reset: () => void
}

export const useSurveyStore = create<SurveyStore>()(
  devtools((set) => ({
    survey: null,
    fields: {},
    currentStep: 'initial',
    history: [],
    retries: 3,
    isPending: false,

    setSurvey: (data) => {
      set({ survey: data }, false, 'setSurvey')
    },

    setStep: (step) => {
      set((state) => {
        if (!state.survey) return {}

        if (hasKey(state.survey.steps, step)) {
          return {
            currentStep: step,
            history: [...state.history, step]
          }
        }

        return {}
      }, false, 'setStep')
    },

    previousStep: () => {
      set((state) => {
        if (state.history.length <= 1) {
          return {
            currentStep: 'initial',
            history: []
          }
        }

        const newHistory = state.history.slice(0, -1)
        const previousStep = newHistory[newHistory.length - 1] ?? 'initial'

        return {
          currentStep: previousStep,
          history: newHistory
        }
      }, false, 'previousStep')
    },

    setField: (field, value) => {
      set((state) => ({
        fields: {
          ...state.fields,
          [field]: value
        }
      }), false, 'setField')
    },

    setArrayField: (field, value) => {
      set((state) => {
        const currentValue = state.fields[field]

        // If the field does not exist or is not an array, create it as a new array
        if (!Array.isArray(currentValue)) {
          return {
            fields: {
              ...state.fields,
              [field]: [value],
            },
          }
        }

        // At this point, currentValue is confirmed to be an array
        const currentValues = currentValue as FieldArrayValue[]
        const includesValue = currentValues.includes(value)

        // If the value already exists, remove it
        if (includesValue) {
          const updatedValues = currentValues.filter((item) => item !== value).sort()

          // If the array becomes empty, remove the field entirely
          if (updatedValues.length === 0) {
            const { [field]: _removed, ...restFields } = state.fields

            return {
              fields: restFields,
            }
          }

          return {
            fields: {
              ...state.fields,
              [field]: updatedValues,
            },
          }
        }

        // If the value does not exist, add it to the array
        return {
          fields: {
            ...state.fields,
            [field]: [...currentValues, value].sort(),
          },
        }
      }, false, 'setArrayField')
    },

    removeRetry: () => {
      set((state) => ({
        retries: state.retries >= 1 ? state.retries - 1 : 0
      }), false, 'removeRetry')
    },

    setIsPending: (value: boolean) => {
      set({ isPending: value }, false, 'setIsPending')
    },

    reset: () => {
      set({
        survey: null,
        fields: {},
        currentStep: 'initial',
        history: [],
        retries: 3,
        isPending: false
      }, false, 'reset')
    }
  }), {
    name: 'survey-store',
    enabled: process.env.NODE_ENV === 'development'
  })
)